import { DataService, getApiStatus } from './data-service.js';
import { safeWrite } from './safe-write.js';
import { apiFetch } from './data-service-api.js';
import { isPricingReadonlyRecomputeEnabled } from '../utils/feature-flags';
import { normalisePricingUnitKey } from '../utils/pricing-unit-key';
import rjBirdRateCardAuthority from '../../backend/src/shared/pricing-core/rate-cards/rj-bird-rate-card-authority.json';
import { preferredLabourCandidates } from './price-list-rate-options.js';
import {
    approveEstimatePricingItem,
    hasEstimateProvenance,
    isEstimatePricingItem,
    isPendingReviewPricingItem,
    isTrustedPricingItem,
    isVisiblePricingItem,
    trustedPricingItems,
} from './pricing-trust.js';

// MIGRATION-FLAG LATCHING FIX (v5.12.0+): previously this module set
// `_pricingMigrationDone = true` BEFORE the backend write had actually
// completed. Under the v5.12.0 API-only contract a write against a stopped
// Docker throws; first-boot-during-outage would latch this flag even though
// zero rows reached Postgres, producing a permanent broken state until the
// page was reloaded. Fix below: only set `_pricingMigrationDone` AFTER the
// await'd save resolves successfully (via safeWrite, which returns { ok }).
let _pricingMigrationDone = false;
let _pricingGetDataInFlight = null;

// ── DEC-PE-010 write-lock (2026-05-31) ──────────────────────────────────────
// The recompute-time writers (addItemFromAI fired once per discovered cost line,
// + batchIncrementUsage) each do a full-snapshot read-modify-write and used to
// fire concurrently/unserialized (cost-recompute.js:412-413). Racing
// read-modify-writes against the wholesale-replace backend lost-updated each
// other and could commit a stale/empty snapshot — the rate-book wipe (DEC-PE-010).
// This lock serialises those critical sections so each reads the prior write's
// committed state (the getData in-flight dedup only coalesces *concurrent* calls;
// serialising them makes each read fresh). The backend guard in
// misc-routes.js is the defense-in-depth safety net; this fixes the race at source.
// IMPORTANT: only wrap OUTER writers (addItemFromAI / batchIncrementUsage) — never
// getData/saveData/migrateItems, which run *inside* the lock; wrapping them would
// re-enter the chain and deadlock.
let _bpWriteLock = Promise.resolve();
function withBpWriteLock(fn) {
    const run = _bpWriteLock.then(fn, fn);
    // Keep the chain alive regardless of this section's outcome so one failed
    // write never wedges every subsequent pricing write.
    _bpWriteLock = run.then(() => {}, () => {});
    return run;
}

// ============================================
// BUSINESS PRICING SERVICE
// Manages business-specific pricing data (materials, labour, equipment, subcontractors)
// ============================================

let _seq = 0;
function genPricingId() {
    _seq = (_seq + 1) >>> 0;
    return 'bp-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 6);
}

function pricingNameField(categoryKey) {
    return (categoryKey === 'labour_rates' || categoryKey === 'subcontractors') ? 'trade' : 'item';
}

function normalisePricingCategory(category) {
    const key = String(category || '').toLowerCase().trim();
    if (key === 'materials' || key === 'material' || key === 'material_costs') return 'material_costs';
    if (key === 'labour' || key === 'labor' || key === 'labour_rates') return 'labour_rates';
    if (key === 'equipment') return 'equipment';
    if (key === 'subcontractor' || key === 'subcontractors') return 'subcontractors';
    return key;
}

function pricingRateField(categoryKey) {
    return (categoryKey === 'labour_rates' || categoryKey === 'subcontractors') ? 'rate' : 'cost';
}

function canonicalPricingTaxBasis(value, fallback = 'ex_gst') {
    const basis = String(value || '').trim().toLowerCase();
    return basis === 'inc_gst' || basis === 'ex_gst' ? basis : fallback;
}

function canonicalPricingCandidate(itemData = {}) {
    const chargeOutTaxBasis = canonicalPricingTaxBasis(
        itemData?.charge_out_tax_basis
            ?? itemData?.chargeOutTaxBasis
            ?? itemData?.rateTaxBasis
            ?? itemData?.rate_tax_basis,
    );
    const costRateTaxBasis = canonicalPricingTaxBasis(
        itemData?.cost_rate_tax_basis ?? itemData?.costRateTaxBasis,
    );
    return {
        ...itemData,
        rateTaxBasis: chargeOutTaxBasis,
        costRateTaxBasis,
        charge_out_tax_basis: chargeOutTaxBasis,
        cost_rate_tax_basis: costRateTaxBasis,
    };
}

function pricingItemName(item = {}, categoryKey) {
    const field = pricingNameField(categoryKey);
    return item[field] || item.name || item.item || item.trade || '';
}

function pricingItemRate(item = {}, categoryKey) {
    const field = pricingRateField(categoryKey);
    const n = Number(item[field] ?? item.rate ?? item.cost);
    return Number.isFinite(n) ? n : null;
}

function pricingItemAliases(item = {}) {
    return Array.isArray(item.aliases)
        ? item.aliases
            .map(alias => String(alias || '').trim())
            .filter(Boolean)
        : [];
}

function normaliseAliasValue(value) {
    return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function cloneReadOnlyPricingValue(value) {
    if (Array.isArray(value)) return value.map(cloneReadOnlyPricingValue);
    if (!value || typeof value !== 'object') return value;
    const cloned = {};
    for (const key of Object.keys(value)) {
        cloned[key] = cloneReadOnlyPricingValue(value[key]);
    }
    return cloned;
}

/**
 * Return a plain, detached snapshot in the canonical field shape without
 * enriching, migrating, seeding, or modifying the object returned by the
 * data layer. This is deliberately separate from getData(): quote evaluation
 * must never turn a read into a pricing write.
 */
function canonicalisePricingSnapshotReadOnly(data) {
    const snapshot = cloneReadOnlyPricingValue(data);
    const pickPopulated = (snakeArr, camelArr) => {
        const snake = Array.isArray(snakeArr) ? snakeArr : [];
        const camel = Array.isArray(camelArr) ? camelArr : [];
        return snake.length >= camel.length ? snake : camel;
    };

    // apiFetch exposes snake_case response aliases as non-enumerable fields.
    // Read both forms from the ORIGINAL before cloning, then write a detached,
    // enumerable canonical property onto the snapshot.
    if (data.materialCosts !== undefined || data.material_costs !== undefined) {
        snapshot.material_costs = cloneReadOnlyPricingValue(
            pickPopulated(data.material_costs, data.materialCosts),
        );
        delete snapshot.materialCosts;
    }
    if (data.labourRates !== undefined || data.labour_rates !== undefined) {
        snapshot.labour_rates = cloneReadOnlyPricingValue(
            pickPopulated(data.labour_rates, data.labourRates),
        );
        delete snapshot.labourRates;
    }
    if (data.businessInfo !== undefined || data.business_info !== undefined) {
        snapshot.business_info = cloneReadOnlyPricingValue(
            data.business_info || data.businessInfo || {},
        );
        delete snapshot.businessInfo;
    }
    if (data.costingConfig !== undefined || data.costing_config !== undefined) {
        snapshot.costingConfig = cloneReadOnlyPricingValue(
            data.costingConfig || data.costing_config || {},
        );
        delete snapshot.costing_config;
    }
    if (data.founderStrategy !== undefined || data.founder_strategy !== undefined) {
        snapshot.founderStrategy = cloneReadOnlyPricingValue(
            data.founderStrategy || data.founder_strategy || {},
        );
        delete snapshot.founder_strategy;
    }
    return snapshot;
}

export class PricingDataUnavailableError extends Error {
    constructor(message = 'Business pricing data is not configured') {
        super(message);
        this.name = 'PricingDataUnavailableError';
        this.code = 'QM_PRICING_DATA_UNAVAILABLE';
    }
}

function enrichedPricingItem(item, categoryKey, now) {
    const source = item.source || 'manual';
    const pending = source === 'ai_discovered' || item.review_status === 'pending_review';
    return {
        ...item,
        id: item.id || genPricingId(),
        created_at: item.created_at || now,
        updated_at: item.updated_at || now,
        usage_count: item.usage_count ?? item.usageCount ?? 0,
        last_used_at: item.last_used_at || item.lastUsedAt || null,
        source,
        created_by: item.created_by || (source === 'ai_discovered' ? 'ai' : 'user'),
        review_status: item.review_status || (pending ? 'pending_review' : 'approved'),
        reviewed_at: item.reviewed_at || item.last_reviewed_at || null,
        reviewed_by: item.reviewed_by || null,
        previous_rate: item.previous_rate ?? null,
        previous_rate_2: item.previous_rate_2 ?? null,
        price_history: Array.isArray(item.price_history) ? item.price_history : [],
        aliases: pricingItemAliases(item),
        pricing_category: item.pricing_category || categoryKey,
    };
}

function recordRateRevision(item, categoryKey, nextRate, { source = 'manual', changedBy = 'user', reason = '' } = {}) {
    const field = pricingRateField(categoryKey);
    const previousRate = pricingItemRate(item, categoryKey);
    const parsedNext = Number(nextRate);
    if (!Number.isFinite(parsedNext)) return item;
    if (previousRate !== null && previousRate === parsedNext) {
        item[field] = parsedNext;
        return item;
    }
    const now = new Date().toISOString();
    const history = Array.isArray(item.price_history) ? item.price_history : [];
    if (previousRate !== null) {
        item.price_history = [
            {
                rate: previousRate,
                unit: item.unit || '',
                changed_at: now,
                changed_by: changedBy,
                source,
                reason,
            },
            ...history,
        ].slice(0, 12);
        item.previous_rate_2 = item.previous_rate ?? null;
        item.previous_rate = previousRate;
    }
    item[field] = parsedNext;
    item.updated_at = now;
    return item;
}

// ============================================================================
// F-04 PATH C — Ambiguity detection in fuzzy matching
// ----------------------------------------------------------------------------
// When the AI emits a compact descriptor like "tiles" or "painting" that
// substring-matches more than one trusted catalogue row (e.g. Tiles (Wall) and
// Tiles (Floor)), the existing matcher would return whichever row sits earlier
// in the usage_count-desc-sorted array — making the cost reproduction
// tenant-history-dependent.
//
// Path C (per docs/ai-pipeline-council/quote-eval-harness/F-04-INVESTIGATION.md
// recommendation): keep the existing fuzzy hit semantics dollar-neutral, but
// when ≥2 candidates pass the includes-check AND their similarity scores fall
// within 10% of each other, mark the result as `confidence: 'ambiguous'` and
// surface the top 3 candidates. The Rate-Trace UI flags ambiguity so Rodney
// can resolve it during Stage 5 review. When one candidate clearly outscores
// the others (≥10% margin on the LCS overlap ratio), it's treated as a
// regular fuzzy hit (current behaviour).
//
// Floor (per F-04 brief): below 50% LCS-overlap (vs the longer side) we skip
// the candidate entirely; below 80% we don't promote to fuzzy on its own —
// BUT we keep the existing `desc.includes(name) || name.includes(desc)`
// short-circuit because that's the dollar-neutral hit rule. Score is used
// for tie-break / margin comparison purposes, not as an inclusion filter on
// boolean-includes hits (which by definition have shorter-side ⊆ longer-side
// and therefore non-trivial overlap).
// ============================================================================

const F04_AMBIGUOUS_MARGIN = 0.10; // ≥2 hits within 10% on overlap score → ambiguous

/**
 * Length of the longest common substring between two strings. O(a*b) DP.
 * Returns 0 for empty inputs.
 */
function _lcsLength(a, b) {
    if (!a || !b) return 0;
    const al = a.length, bl = b.length;
    let best = 0;
    // Rolling 1-D DP — only need previous row.
    let prev = new Array(bl + 1).fill(0);
    let curr = new Array(bl + 1).fill(0);
    for (let i = 1; i <= al; i++) {
        for (let j = 1; j <= bl; j++) {
            if (a.charCodeAt(i - 1) === b.charCodeAt(j - 1)) {
                curr[j] = prev[j - 1] + 1;
                if (curr[j] > best) best = curr[j];
            } else {
                curr[j] = 0;
            }
        }
        const swap = prev; prev = curr; curr = swap;
        // reset curr for the next pass without re-allocating
        for (let k = 0; k <= bl; k++) curr[k] = 0;
    }
    return best;
}

/**
 * Normalised overlap score: LCS-length / max(|a|,|b|). Range [0..1].
 */
function _overlapScore(a, b) {
    if (!a || !b) return 0;
    const lcs = _lcsLength(a, b);
    return lcs / Math.max(a.length, b.length);
}

/**
 * Pick the top fuzzy candidate from a candidate list, or signal ambiguity.
 *
 * @param {string} desc       Normalised descriptor (already lowercased + trimmed)
 * @param {Array}  candidates Array of { row, name } pairs that passed
 *                            `desc.includes(name) || name.includes(desc)`.
 *                            Pre-sorted by usage_count descending.
 * @param {Function} buildResult  Function (row, confidence) → result object.
 *                            Used to construct fuzzy / ambiguous return shape
 *                            with the per-category id/rate/unit field names.
 * @param {Function} buildCandidate Function (row, name) → candidate descriptor
 *                            (id/name/rate/unit) for the `candidates` array on
 *                            the ambiguous result.
 * @returns {Object|null}     `null` if no candidates, `{ confidence: 'fuzzy', ... }`
 *                            if one or a clear winner, `{ confidence: 'ambiguous',
 *                            candidates: [...], pricingItemId: null, matchedName:
 *                            null, matchedRate: null, matchedUnit: null }` if ≥2
 *                            within 10% margin.
 */
function _resolveFuzzyCandidates(desc, candidates, buildResult, buildCandidate) {
    if (!candidates || candidates.length === 0) return null;

    // Single hit → keep current behaviour (fuzzy).
    if (candidates.length === 1) {
        return buildResult(candidates[0].row, 'fuzzy');
    }

    // ≥2 hits → score each, decide winner-or-ambiguous.
    const scored = candidates.map(c => ({
        row: c.row,
        name: c.name,
        score: _overlapScore(desc, c.name),
        usage: Number(c.row.usage_count) || 0,
    }));

    // Sort by score desc; tie-break by usage_count desc (matches existing
    // first-of-equals semantics for candidates that score identically).
    scored.sort((a, b) => (b.score - a.score) || (b.usage - a.usage));

    const top = scored[0];
    const second = scored[1];
    const margin = top.score - second.score;

    if (margin >= F04_AMBIGUOUS_MARGIN) {
        // Clear winner — fuzzy hit on the top-scorer.
        return buildResult(top.row, 'fuzzy');
    }

    // Ambiguous: top 3 candidates surfaced for Rate-Trace UI.
    const top3 = scored.slice(0, 3).map(s => buildCandidate(s.row, s.name));
    return {
        pricingItemId: null,
        matchedName: null,
        matchedRate: null,
        matchedUnit: null,
        confidence: 'ambiguous',
        candidates: top3,
    };
}

// ============================================================================
// F-04 PATH D — Variant-tolerant compact form for catalogue matching
// ----------------------------------------------------------------------------
// Real-world finding from Vaucluse Tower St (RBB/TSV) v5.19.35 E2E test:
// the AI emits cost-line names like "Waterproofer" (one word) while the
// catalogue ships "Water Proofer" (two words). The original `desc.includes(name)
// || name.includes(desc)` test is dollar-neutral but case-and-whitespace
// sensitive, so the AI's emission and the catalogue entry don't link even
// though the AI is plainly using the catalogue rate ($110/hr). Result: every
// labour line on a real Vaucluse roof-repair quote shows AI-SUGGESTED in the
// Rate-Trace UI despite the catalogue being the obvious source.
//
// Path D (added 2026-05-08 after the live test surfaced the gap): introduce
// a *compact form* normalisation that strips whitespace, parenthetical
// qualifiers, and non-alphanumerics on both sides of the comparison. Insert
// a new compact-exact match pass BEFORE the substring fuzzy pass:
//
//   Pass 1 — exact (current `norm()` form):                "tile"  === "tile"
//   Pass 2 — compact-exact (NEW, Path D):                  "waterproofer" === "water proofer".compact
//   Pass 2b — plural-tolerant compact-exact (NEW):         "tile" === "tiles".compactNoPlural
//   Pass 3 — substring fuzzy (current, F-04 Path C):       "primer" within "primer/sealer (4l)"
//   Pass 4 — ambiguous (current, F-04 Path C):             ≥2 hits with similar overlap
//
// Compact normalisation rules:
//   • lowercase
//   • drop parenthetical content `(…)` (catches unit qualifiers like "(4L)",
//     "(20kg)", "(Wall)" so the core name compares cleanly)
//   • strip everything non-alphanumeric (whitespace, punctuation, hyphens,
//     slashes — catches "Air-Conditioning" ↔ "Air Conditioning")
//   • plural-tolerant variant additionally drops a trailing 's' on words
//     ≥4 chars (catches "Tiles" ↔ "Tile" without false-stripping
//     short words like "as" or "is")
//
// Why compact-exact before substring: substring is a wide net that can
// surface multiple loose matches and trigger the F-04-Path-C ambiguity
// path. Compact-exact is a *strict* match (the cores are identical after
// whitespace + punctuation normalisation) — when it fires, it is the right
// answer with high confidence even if cosmetic spelling differs. Ambiguity
// only fires if multiple catalogue entries share the same compact core.
//
// Why this is safe: compact-exact requires the *entire* normalised string
// to match. It cannot pull in unrelated entries the way substring can —
// "Cement" → "cement" only equals "Cement (20kg)" → "cement" because
// they share an identical core after paren-strip. The unit suffix that
// the matcher returns (matchedUnit) is the catalogue's unit, so cost-
// recompute consumers can still apply unit-compatibility checks downstream.
// ============================================================================

/**
 * Strip-everything compact form: lowercase, drop parenthetical qualifiers,
 * strip whitespace + non-alphanumerics. Used as the strict-equality key for
 * the Path D variant-tolerant match pass.
 *
 * Examples:
 *   _compactName("Water Proofer")              → "waterproofer"
 *   _compactName("Waterproofer")               → "waterproofer"
 *   _compactName("Air-Conditioning")           → "airconditioning"
 *   _compactName("Primer/Sealer (4L)")         → "primersealer"
 *   _compactName("Cement (20kg)")              → "cement"
 *   _compactName("Tiles (Wall)")               → "tiles"
 */
function _compactName(s) {
    if (!s) return '';
    return String(s)
        .toLowerCase()
        .replace(/\([^)]*\)/g, ' ')      // drop parenthetical content
        .replace(/[^a-z0-9]+/g, '');     // strip everything non-alphanumeric
}

/**
 * Plural-tolerant compact form: same as `_compactName` but additionally drops
 * a trailing 's' when the resulting stem is ≥4 chars (avoids butchering short
 * words like "as", "is", "us"). Used as a secondary equality key so "Tiles"
 * matches "Tile" and "Membranes" matches "Membrane".
 */
function _compactNoPlural(s) {
    const c = _compactName(s);
    return c.length >= 4 && c.endsWith('s') ? c.slice(0, -1) : c;
}

/**
 * Path D pass — gather catalogue entries whose compact form matches the
 * input's compact form (or plural-tolerant variant). Returns an array of
 * { row, name } pairs in the same shape `_resolveFuzzyCandidates` consumes,
 * so a 1-hit result returns confidence: 'fuzzy' and a multi-hit returns
 * confidence: 'ambiguous' with candidates surfaced for the Rate-Trace UI.
 *
 * @param {string} input The user/AI-emitted descriptor.
 * @param {Array}  rows  Trusted catalogue items.
 * @param {Function} getName Function (row) → catalogue label string.
 * @returns {Array<{row, name}>} hits, ordered by usage_count desc.
 */
function _findCompactMatches(input, rows, getName) {
    const inputCompact = _compactName(input);
    const inputSingular = _compactNoPlural(input);
    if (!inputCompact || inputCompact.length < 2) return [];
    const hits = [];
    for (const row of rows) {
        const label = getName(row);
        const labelCompact = _compactName(label);
        if (!labelCompact || labelCompact.length < 2) continue;
        // Strict compact-exact OR plural-tolerant compact-exact.
        if (labelCompact === inputCompact || _compactNoPlural(label) === inputSingular) {
            hits.push({ row, name: (label || '').toLowerCase().replace(/\s+/g, ' ').trim() });
        }
    }
    // Sort by usage_count desc to mirror the existing fuzzy-pass tie-break.
    hits.sort((a, b) => (Number(b.row.usage_count) || 0) - (Number(a.row.usage_count) || 0));
    return hits;
}

function _findAliasMatch(input, rows, getName, categoryKey) {
    const inputNorm = normaliseAliasValue(input);
    const inputCompact = _compactName(input);
    if (!inputNorm || inputNorm.length < 2) return null;

    const hits = [];
    for (const row of rows) {
        const aliases = pricingItemAliases(row);
        if (!aliases.length) continue;
        const matchedAlias = aliases.find(alias => {
            if (normaliseAliasValue(alias) === inputNorm) return true;
            return inputCompact
                && inputCompact.length >= 2
                && _compactName(alias) === inputCompact;
        });
        if (matchedAlias) {
            hits.push({
                row,
                name: normaliseAliasValue(getName(row)),
                alias: matchedAlias,
            });
        }
    }

    if (hits.length === 0) return null;
    hits.sort((a, b) => (Number(b.row.usage_count) || 0) - (Number(a.row.usage_count) || 0));
    if (hits.length === 1) return hits[0];

    return {
        ambiguousAlias: true,
        candidates: hits.slice(0, 3).map(hit => ({
            pricingItemId: hit.row.id || null,
            name: getName(hit.row),
            alias: hit.alias,
            rate: pricingItemRate(hit.row, categoryKey),
            unit: hit.row.unit,
        })),
    };
}

// Fields that enrichedPricingItem() fills in. migrateItems compares an item
// against its enriched form ON THESE FIELDS ONLY.
//
// 2026-08-20: the old test was `JSON.stringify(next) !== JSON.stringify(item)`,
// which can never be false for a book that arrived through apiFetch. The
// incoming item carries camelCase enumerable keys (createdAt, usageCount,
// reviewStatus, ...); enrichedPricingItem's spread copies those and then
// APPENDS the snake_case set, so the two strings always differ, `changed` was
// always true, and EVERY getData() POSTed the whole rate book. That is the
// ~8-saves-in-seconds burst seen during batch costing, each one tripping the
// DEC-PE-010 empty-overwrite guard on the backend
// (backend/src/routes/misc-routes.js:343-348).
//
// Comparing the managed fields BY VALUE is idempotent: `item[field]` reads the
// snake_case name whether it is a real own key (Dexie / getDefaultData) or an
// apiFetch non-enumerable alias (data-service-api.js:129-138).
const ENRICHED_ITEM_FIELDS = [
    'id', 'created_at', 'updated_at', 'usage_count', 'last_used_at',
    'source', 'created_by', 'review_status', 'reviewed_at', 'reviewed_by',
    'previous_rate', 'previous_rate_2', 'price_history', 'aliases',
    'pricing_category',
];

function enrichmentAddedAnything(item, next) {
    return ENRICHED_ITEM_FIELDS.some((field) => {
        const before = item?.[field];
        const after = next[field];
        if (Array.isArray(before) || Array.isArray(after)) {
            // price_history passes through by reference when it is already an
            // array, and aliases is rebuilt from the same strings in the same
            // order, so a difference here is a real one.
            return JSON.stringify(before ?? null) !== JSON.stringify(after ?? null);
        }
        return before !== after;
    });
}

/** True when the last write failed with an unreachable-class error (502/503/504,
 *  network). Never throws: a broken status read must not break pricing. */
function apiKnownUnreachable() {
    try { return getApiStatus().disabled === true; } catch { return false; }
}

/**
 * Migrate pricing items to include IDs, timestamps, and usage tracking.
 * Idempotent: only adds missing fields, never overwrites existing ones.
 */
async function migrateItems(data) {
    if (!data) return data;
    const now = data.updated_at || new Date().toISOString();
    const categories = ['material_costs', 'labour_rates', 'equipment', 'subcontractors'];
    let changed = false;
    for (const cat of categories) {
        if (!Array.isArray(data[cat])) continue;
        data[cat] = data[cat].map(item => {
            const next = enrichedPricingItem(item, cat, now);
            if (enrichmentAddedAnything(item, next)) changed = true;
            return next;
        });
    }
    // Skip only the WRITE while the backend is known unreachable — the
    // enrichment above already happened in memory, so matching and usage
    // tracking behave normally for this call.
    //
    // 2026-08-21: without this, the migration write is self-sustaining during
    // an outage. The read path falls back to stale Dexie on a 502, so getData()
    // keeps SUCCEEDING, keeps re-deriving changed === true (the enrichment was
    // never persisted, so it is recomputed every time), and keeps re-attempting
    // a write that cannot land — once per getData(), and the matcher loads the
    // book repeatedly during a pipeline run. getApiStatus() is set from real
    // unreachable-class write failures and cleared on the next success, so the
    // migration persists itself the first time the backend answers again.
    if (changed && !apiKnownUnreachable()) {
        // Await save so migrated IDs/timestamps persist reliably. safeWrite
        // surfaces a toast on failure (attribution: 'businessPricing.save(item migration)'),
        // since this write backs IDs used by downstream usage-count tracking.
        // silent loss would mis-count usage permanently.
        await safeWrite(
            DataService.businessPricing.save({ ...data, id: 'default', updated_at: new Date().toISOString() }),
            { what: 'businessPricing.save(item migration)' },
        );
    }
    return data;
}

export const BusinessPricingService = {
    isEstimatePricingItem,
    isPendingReviewPricingItem,
    isTrustedPricingItem,
    isVisiblePricingItem,

    /**
     * Load an honest, detached rate-book snapshot without any write-capable
     * fallback. Unlike getData(), this method NEVER checks Dexie, seeds the
     * RJ Bird defaults, enriches rows, migrates schemas, or saves data.
     * Transport errors always propagate. A genuinely missing row returns null
     * unless failIfMissing is true, in which case evaluation fails closed.
     */
    async getDataReadOnly({ failIfMissing = false } = {}) {
        const data = await DataService.businessPricing.get({ strictReadOnly: true });
        if (!data) {
            if (failIfMissing) throw new PricingDataUnavailableError();
            return null;
        }
        return canonicalisePricingSnapshotReadOnly(data);
    },

    /**
     * A strict read-only snapshot in the shape the Pricing page edits. Pure: no I/O, no seeding, no write.
     *
     * getData() hands the page rows that carry the snake_case bookkeeping fields (review_status,
     * usage_count, price_history, pricing_category ...) which apiFetch exposes only as non-enumerable
     * aliases, and then enriches them. The detached read-only snapshot has lost those aliases, so a page
     * that adopted it as it stands would see rows without a review status or a price history. This
     * restores each snake_case field from its camelCase twin (never overriding a value the row already
     * has) and applies the same enrichment getData applies, so the page shows exactly what it shows after
     * a normal load, without the read-modify-write that load can perform.
     */
    toEditingSnapshot(snapshot) {
        if (!snapshot || typeof snapshot !== 'object') return snapshot;
        const stamp = typeof snapshot.updatedAt === 'string' && snapshot.updatedAt
            ? snapshot.updatedAt
            : (snapshot.updated_at || new Date().toISOString());
        const camelTwin = (field) => field.replace(/_([a-z0-9])/g, (_match, letter) => letter.toUpperCase());
        const adapted = { ...snapshot };
        for (const category of ['material_costs', 'labour_rates', 'equipment', 'subcontractors']) {
            if (!Array.isArray(snapshot[category])) continue;
            adapted[category] = snapshot[category].map((item) => {
                if (!item || typeof item !== 'object') return item;
                const withTwins = { ...item };
                for (const field of ENRICHED_ITEM_FIELDS) {
                    const twin = camelTwin(field);
                    if (withTwins[field] === undefined && twin !== field && item[twin] !== undefined) {
                        withTwins[field] = item[twin];
                    }
                }
                return enrichedPricingItem(withTwins, category, stamp);
            });
        }
        return adapted;
    },

    /**
     * Load the tenant rate book.
     *
     * `failHard: true` (2026-07-03) — rethrow a READ FAILURE instead of
     * answering with the populated default book. Callers that EDIT + SAVE
     * the whole book (Pricing Console, worktable write-back, the costing /
     * strategy / review composites) MUST pass it: editing silently-
     * substituted defaults and saving persists them OVER the operator's
     * real rates, and the DEC-PE-010 backend guard cannot catch populated
     * wrong arrays. Read-only prompt/costing consumers keep the soft
     * default (a defaults answer degrades one AI generation, loses nothing).
     * A genuinely-missing row (backend 200+null) is NOT a failure — the
     * seed path still runs for fresh tenants in both modes.
     */
    async getData({ failHard = false } = {}) {
        if (!_pricingGetDataInFlight) {
            _pricingGetDataInFlight = (async () => {
        {
            let data = await DataService.businessPricing.get();
            if (data) {
                // ── Schema canonicalisation (snake_case is canonical) ──
                // 2026-05-08: stale legacy records can carry BOTH camelCase
                // (`materialCosts`) AND snake_case (`material_costs`) in the
                // same row. The OLD normalisation only copied camelCase →
                // snake_case when snake_case was undefined; an empty array
                // (`material_costs: []`) blocked the copy and silently
                // dropped the camelCase data. Fix: prefer whichever side is
                // populated, then DELETE the dead camelCase keys so writes
                // back to PG don't perpetuate the dual-key anomaly.
                const _pickPopulated = (snakeArr, camelArr) => {
                    const s = Array.isArray(snakeArr) ? snakeArr : [];
                    const c = Array.isArray(camelArr) ? camelArr : [];
                    return s.length >= c.length ? s : c;
                };
                // 2026-08-20 (DEC-PE-010 guard-spam root cause): apiFetch's
                // convertKeysToCamel exposes every snake_case response key as a
                // NON-ENUMERABLE alias (data-service-api.js:129-138). A plain
                // assignment to an EXISTING property does NOT change
                // [[Enumerable]], so `data.material_costs = ...` wrote INTO that
                // hidden slot: the array read back correctly in memory but was
                // invisible to JSON.stringify and to object spread. Every later
                // POST therefore omitted material_costs / labour_rates, the
                // backend coerced them to [] (misc-routes.js:280-281) and the
                // DEC-PE-010 preserve guard fired on every save. Deleting the
                // alias first (it is `configurable: true`) makes the assignment
                // create a fresh own ENUMERABLE property; the value is evaluated
                // BEFORE the delete, so nothing is lost.
                const _setCanonical = (obj, key, value) => {
                    delete obj[key];
                    obj[key] = value;
                };
                if (data.materialCosts !== undefined || data.material_costs !== undefined) {
                    _setCanonical(data, 'material_costs', _pickPopulated(data.material_costs, data.materialCosts));
                    delete data.materialCosts;
                }
                if (data.labourRates !== undefined || data.labour_rates !== undefined) {
                    _setCanonical(data, 'labour_rates', _pickPopulated(data.labour_rates, data.labourRates));
                    delete data.labourRates;
                }
                if (data.businessInfo !== undefined || data.business_info !== undefined) {
                    _setCanonical(data, 'business_info', data.business_info || data.businessInfo || {});
                    delete data.businessInfo;
                }
                // costingConfig / founderStrategy canonicalise to camelCase, which
                // is already the ENUMERABLE key apiFetch produced: no alias
                // surgery needed.
                if (data.costing_config && !data.costingConfig) data.costingConfig = data.costing_config;
                if (data.costing_config !== undefined && data.costingConfig) delete data.costing_config;
                if (data.founder_strategy && !data.founderStrategy) data.founderStrategy = data.founder_strategy;
                if (data.founder_strategy !== undefined && data.founderStrategy) delete data.founder_strategy;

                // A quote-only user receives a server-owned sell-only projection.
                // It is already canonical and must remain a terminal read result:
                // falling through to the legacy Dexie migration or default-seed
                // paths could substitute cached/full pricing data and attempt a
                // write that this user is deliberately not authorised to make.
                if (data.accessProjection === 'sell_only' || data.access_projection === 'sell_only') {
                    return data;
                }

                // ── One-time Dexie → PG migration ──
                // 2026-05-08: previous heuristic treated ANY non-empty
                // `equipment` array as "has real data", which means a single
                // AI-discovered scaffold row blocks the seed/migration path
                // and leaves materials + labour permanently empty. Use
                // trusted-only counts so AI-discovered / pending-review rows
                // don't gate the migration.
                const trustedMaterials = trustedPricingItems(data.material_costs).length;
                const trustedLabour = trustedPricingItems(data.labour_rates).length;
                const trustedCount = trustedMaterials + trustedLabour
                    + trustedPricingItems(data.equipment).length
                    + trustedPricingItems(data.subcontractors).length;
                // DEC-PE-010 (2026-05-31): a book whose CORE catalogue (materials
                // + labour) is empty needs (re-)seed even when equipment /
                // subcontractors survived. Before this, a wiped book (0 materials
                // / 0 labour / N equipment — the exact rate-book-wipe signature)
                // never self-healed: surviving equipment kept trustedCount > 0, so
                // hasRealData stayed true and the seed below was suppressed,
                // leaving the rate sheet permanently empty until manual repair.
                // Defaults are deliberately tenant-neutral. Customer-specific
                // catalogues are applied only from an authenticated, explicit
                // tenant-owned workflow in the Pricing page.
                const coreCatalogueEmpty = trustedMaterials === 0 && trustedLabour === 0;
                const hasRealData = trustedCount > 0 && !coreCatalogueEmpty;
                if (!hasRealData && !data._dexieMigrationChecked && !_pricingMigrationDone) {
                    try {
                        const { db } = await import('./db.js');
                        const dexieData = await db.business_pricing.get('default');
                        if (dexieData) {
                            const dexieTrustedCount = trustedPricingItems(dexieData.material_costs).length
                                + trustedPricingItems(dexieData.labour_rates).length
                                + trustedPricingItems(dexieData.equipment).length
                                + trustedPricingItems(dexieData.subcontractors).length;
                            if (dexieTrustedCount > 0) {
                                console.log('[Pricing] Migrating pricing data from Dexie → PostgreSQL');
                                const migrated = { ...dexieData, id: 'default', updated_at: new Date().toISOString() };
                                // Strip stale camelCase keys before writing back.
                                delete migrated.materialCosts;
                                delete migrated.labourRates;
                                delete migrated.businessInfo;
                                delete migrated.costing_config;
                                delete migrated.founder_strategy;
                                // Await the save + only latch the flag on success.
                                // If the write fails, next session retries instead
                                // of sitting in a permanent broken state.
                                const { ok } = await safeWrite(
                                    DataService.businessPricing.save(migrated),
                                    { what: 'businessPricing.save(dexie→pg migration)', context: { flow: 'hasData path' } },
                                );
                                if (ok) _pricingMigrationDone = true;
                                return migrateItems(migrated) || migrated;
                            }
                        }
                    } catch (migErr) {
                        if (import.meta.env.DEV) console.warn('[Pricing] Dexie migration check failed:', migErr?.message);
                    }
                    // ── No trusted data anywhere → seed defaults ──
                    // 2026-05-08: the previous code path latched
                    // `_pricingMigrationDone = true` here and returned the
                    // empty record, leaving the rate sheet permanently empty
                    // until manual intervention. Now: if both PG and Dexie
                    // have no trusted data, seed defaults so the matcher has
                    // something to match against. The AI-discovered fragments
                    // (if any) get cleared; defaults take their place.
                    try {
                        const defaults = this.getDefaultData();
                        const defaultTrustedCount = trustedPricingItems(defaults.material_costs).length
                            + trustedPricingItems(defaults.labour_rates).length;
                        if (defaultTrustedCount === 0) return migrateItems(data) || data;
                        const seeded = {
                            ...defaults,
                            // DEC-PE-010: MERGE, don't clobber. Restore the empty
                            // core (materials + labour) from defaults, but PRESERVE
                            // any surviving equipment / subcontractors — a custom or
                            // partially-populated book keeps its real data instead of
                            // being overwritten with the RJ Bird defaults.
                            equipment: trustedPricingItems(data.equipment).length > 0 ? data.equipment : defaults.equipment,
                            subcontractors: trustedPricingItems(data.subcontractors).length > 0 ? data.subcontractors : defaults.subcontractors,
                            id: 'default',
                            updated_at: new Date().toISOString(),
                        };
                        const { ok } = await safeWrite(
                            DataService.businessPricing.save(seeded),
                            { what: 'businessPricing.save(seed defaults — no trusted data)', silent: true },
                        );
                        if (ok) {
                            _pricingMigrationDone = true;
                            return migrateItems(seeded) || seeded;
                        }
                    } catch (seedErr) {
                        if (import.meta.env.DEV) console.warn('[Pricing] Default-seed write failed:', seedErr?.message);
                    }
                    // Seed write failed → don't latch (next session retries).
                }

                // If a stale empty row survived after the migration latch was
                // already set, do not return `{}` as a valid rate sheet. That
                // leaves Stage 4/5 pricing audit panels with a non-null but
                // empty catalogue, so every otherwise-valid cost line appears
                // AI-suggested. Seed deterministic defaults instead.
                if (!hasRealData) {
                    try {
                        const defaults = this.getDefaultData();
                        const defaultTrustedCount = trustedPricingItems(defaults.material_costs).length
                            + trustedPricingItems(defaults.labour_rates).length;
                        if (defaultTrustedCount === 0) return migrateItems(data) || data;
                        const seeded = {
                            ...defaults,
                            // DEC-PE-010: MERGE, don't clobber. Restore the empty
                            // core (materials + labour) from defaults, but PRESERVE
                            // any surviving equipment / subcontractors — a custom or
                            // partially-populated book keeps its real data instead of
                            // being overwritten with the RJ Bird defaults.
                            equipment: trustedPricingItems(data.equipment).length > 0 ? data.equipment : defaults.equipment,
                            subcontractors: trustedPricingItems(data.subcontractors).length > 0 ? data.subcontractors : defaults.subcontractors,
                            id: 'default',
                            updated_at: new Date().toISOString(),
                        };
                        const { ok } = await safeWrite(
                            DataService.businessPricing.save(seeded),
                            { what: 'businessPricing.save(seed defaults - stale empty row)', silent: true },
                        );
                        // DEC-PE-010: only return the seeded view if it actually
                        // persisted; otherwise fall through to the honest current
                        // state below (the next getData retries the seed) rather
                        // than showing a populated sheet that was never saved.
                        if (ok) return migrateItems(seeded) || seeded;
                    } catch (seedErr) {
                        if (import.meta.env.DEV) console.warn('[Pricing] Default-seed write failed for stale empty row:', seedErr?.message);
                    }
                }

                return migrateItems(data) || data;
            }
            // No data in DB at all - check Dexie first, then seed with defaults
            if (!_pricingMigrationDone) {
                try {
                    const { db } = await import('./db.js');
                    const dexieData = await db.business_pricing.get('default');
                    if (dexieData && ((dexieData.material_costs?.length > 0) || (dexieData.labour_rates?.length > 0))) {
                        console.log('[Pricing] Found pricing data in Dexie - pushing to PostgreSQL');
                        const migrated = { ...dexieData, id: 'default', updated_at: new Date().toISOString() };
                        // Only latch the migration flag if the write succeeded.
                        const { ok } = await safeWrite(
                            DataService.businessPricing.save(migrated),
                            { what: 'businessPricing.save(dexie→pg migration)', context: { flow: 'noBackendData path' } },
                        );
                        if (ok) _pricingMigrationDone = true;
                        return migrateItems(migrated) || migrated;
                    }
                } catch { /* Dexie unavailable - proceed to defaults */ }
                _pricingMigrationDone = true;
            }

            const defaults = this.getDefaultData();
            // Tenant-neutral defaults intentionally contain no catalogue. Do
            // not create or mutate a production tenant row merely because the
            // page was opened; customer rate books are applied explicitly.
            return defaults;
        }
            })().finally(() => { _pricingGetDataInFlight = null; });
        }

        // Per-caller failure policy OUTSIDE the shared in-flight promise: the
        // promise itself now genuinely REJECTS on a read failure (the old
        // terminal catch inside it returned getDefaultData(), which made a
        // failed read indistinguishable from a real book — the Console then
        // edited + persisted defaults over the operator's rates).
        try {
            return await _pricingGetDataInFlight;
        } catch (e) {
            console.warn('BusinessPricingService.getData error:', e);
            if (failHard) throw e;
            return this.getDefaultData();
        }
    },

    async saveData(data, { allowEmptyArrays = false } = {}) {
        try {
            // DEC-PE-010: `allowEmptyArrays` is sent ONLY by the authoritative
            // Pricing Console (a deliberate user edit — e.g. deleting the last
            // item in a category) so the backend guard lets a genuine clear
            // through. Automated writers (addItemFromAI / batchIncrementUsage /
            // self-heal) never pass it, so they can never empty the book.
            const payload = allowEmptyArrays ? { ...data, _allowEmptyArrays: true } : data;
            await DataService.businessPricing.save(payload);
            // v5.32.2: broadcast a `qm-pricing-updated` window event so any
            // mount-only consumer of BusinessPricingService can re-fetch.
            // Mount-only state-init was bitten by a real stale-rates bug —
            // user edits a rate in BusinessTrainingPage, returns to a
            // QM Assistant tab whose `businessPricing` was captured at
            // page load, fires a cost-recompute, AI sees the OLD rates.
            // Same pattern as the qm-quote-scope-replace event in App.jsx —
            // a single broadcast point, every consumer adds its own
            // refresh effect. Detail carries no payload (consumers do a
            // fresh getData() — keeps event lightweight + avoids stale
            // copies racing).
            if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
                try {
                    window.dispatchEvent(new CustomEvent('qm-pricing-updated', {
                        detail: { source: 'BusinessPricingService.saveData', at: Date.now() },
                    }));
                } catch { /* CustomEvent unsupported in this env; silent */ }
            }
        } catch (e) {
            console.error('BusinessPricingService.saveData error:', e);
            // Re-throw so callers can handle or surface the error to the user
            throw e;
        }
    },

    getDefaultData() {
        const legacyDefaults = {
            id: 'default',
            // IDENTITY IS BLANK ON PURPOSE (2026-08-17).
            //
            // These were Customer Zero's real trading name, ABN and builder
            // licences, handed to any client whose rate book had not loaded.
            // Anything that then saved the book wrote another company's
            // regulated identity into that client's own record, and a quote
            // rendered from it would carry the wrong ABN and licence.
            //
            // Blank is the correct default: the tenant's real identity lives in
            // tenant_profiles (migration 095) and is filled during onboarding. A
            // missing name is a visible gap; a borrowed one is a document that
            // looks right and is not.
            business: {
                name: "",
                abn: "",
                licence_no: "",
                licence_no_2: "",
                address: "",
                phone: "",
                email: "",
                memberships: ["Master Builders Association", "HIA"],
                services: ["Building Maintenance", "Project Management", "Renovations", "Insurance Work", "Strata Work"],
                fully_insured: true,
                // Jurisdiction code - valid values come from standards-library.js
                // (AU-NSW, AU-VIC, AU-QLD, AU-SA, AU-WA, AU-TAS, AU-NT, AU-ACT, AU-default).
                // Drives locality-aware prompt injection and applicable-standards selection.
                primary_region: "AU-NSW",
                region_note: ""
            },
            // ─── Materials catalogue ──────────────────────────────────────
            // Source of truth: RJBIRD_Pricing_Schedule.xlsx (Peter, 2026-05-20).
            // 8 AI-Review items + Rate-Book catalogue + 16 additional Rate-Book
            // items added in this revision. Optional `source` field tags
            // provenance ("AI Review" | "Rate Book" | "") for operator audit.
            material_costs: [
                { item: "Wall tiles (matching existing)", unit: "Per square meter", cost: 40.00, source: "AI Review" },
                { item: "Tile adhesive, grout, and silicone", unit: "Each", cost: 150.00, source: "AI Review" },
                { item: "Crimsafe security fly screens (custom sized)", unit: "Each", cost: 360.00, source: "AI Review" },
                { item: "Premium solid timber parquetry (herringbone pattern, incl. 10% waste)", unit: "Per square meter", cost: 135.00, source: "AI Review" },
                { item: "Premium polyurethane flooring adhesive", unit: "Per square meter", cost: 18.00, source: "AI Review" },
                { item: "Moisture barrier / primer", unit: "Per square meter", cost: 6.00, source: "AI Review" },
                { item: "Premium multi-coat finish system (materials)", unit: "Per square meter", cost: 25.00, source: "AI Review" },
                { item: "Skirting fixings, patching compound, and touch-up paint", unit: "Each", cost: 250.00, source: "AI Review" },
                { item: "Tiles (Wall)", unit: "Per square meter", cost: 40.00, source: "Rate Book" },
                { item: "Tiles (Floor)", unit: "Per square meter", cost: 70.00, source: "Rate Book" },
                { item: "Bricks (Supply)", unit: "Each", cost: 4.00, source: "Rate Book" },
                { item: "Bricks (Laying)", unit: "Each", cost: 4.00, source: "Rate Book" },
                { item: "Excavations", unit: "Per square meter", cost: 160.00, source: "Rate Book" },
                { item: "Concrete (Supply and lay)", unit: "Per square meter", cost: 160.00, source: "Rate Book" },
                { item: "Plasterboard (Supply and install)", unit: "Per square meter", cost: 90.00, source: "Rate Book" },
                { item: "Fire rated plasterboard (Supply and install)", unit: "Per square meter", cost: 120.00, source: "Rate Book" },
                { item: "Rendering", unit: "Per square meter", cost: 90.00, source: "Rate Book" },
                { item: "Painting (Internal)", unit: "Per square meter", cost: 90.00, source: "Rate Book" },
                { item: "Painting (External)", unit: "Per square meter", cost: 100.00, source: "Rate Book" },
                { item: "Metal Roofing (Supply and install)", unit: "Per square meter", cost: 160.00, source: "Rate Book" },
                { item: "Tile Roofing (Supply and install)", unit: "Per square meter", cost: 350.00, source: "Rate Book" },
                { item: "Timber windows (Supply and install)", unit: "Per square meter", cost: 1400.00, source: "Rate Book" },
                { item: "Aluminium windows (Supply and install)", unit: "Per square meter", cost: 500.00, source: "Rate Book" },
                { item: "Pebble crete (Supply and install)", unit: "Per square meter", cost: 350.00, source: "Rate Book" },
                { item: "Carpet (Supply and install)", unit: "Per square meter", cost: 120.00, source: "Rate Book" },
                { item: "Kitchen Cupboards (Each)", unit: "Per cupboard", cost: 600.00, source: "Rate Book" },
                { item: "Timber Fencing (Supply and install)", unit: "Per linear meter", cost: 220.00, source: "Rate Book" },
                { item: "Colorbond fencing (Supply and install)", unit: "Per linear meter", cost: 160.00, source: "Rate Book" },
                { item: "Water proofing (Torch on)", unit: "Per square meter", cost: 140.00, source: "Rate Book" },
                { item: "Water proofing (PVC)", unit: "Per square meter", cost: 190.00, source: "Rate Book" },
                { item: "Engineered tile screed", unit: "Per square meter", cost: 170.00, source: "Rate Book" },
                { item: "Cement tile screed", unit: "Per square meter", cost: 130.00, source: "Rate Book" },
                { item: "Air conditioner (Relocation fee)", unit: "Per unit", cost: 770.00, source: "Rate Book" },
                { item: "Structural pine (90x45)", unit: "Per linear meter", cost: 9.00, source: "Rate Book" },
                { item: "Structural treated pine (240x45)", unit: "Per linear meter", cost: 15.00, source: "Rate Book" },
                { item: "Structural treated pine (140x45)", unit: "Per linear meter", cost: 18.00, source: "Rate Book" },
                { item: "Treat pine decking (90x18)", unit: "Per linear meter", cost: 8.00, source: "Rate Book" },
                { item: "Hybrid decking (137x23)", unit: "Per linear meter", cost: 50.00, source: "Rate Book" },
                { item: "H3 Fascia (180x18)", unit: "Per linear meter", cost: 25.00, source: "Rate Book" },
                { item: "H3 treated primed (V) joint lining boards", unit: "Per linear meter", cost: 50.00, source: "Rate Book" },
                { item: "H3 treated (31x31)", unit: "Per linear meter", cost: 33.00, source: "Rate Book" },
                { item: "H3 treated primed (90x45)", unit: "Per linear meter", cost: 11.00, source: "Rate Book" },
                { item: "H3 treated primed (66x42)", unit: "Per linear meter", cost: 8.00, source: "Rate Book" },
                { item: "H3 treated primed (88x42)", unit: "Per linear meter", cost: 16.00, source: "Rate Book" },
                { item: "H3 treated primed (88x88)", unit: "Per linear meter", cost: 32.00, source: "Rate Book" },
                { item: "H3 treated primed (110x42)", unit: "Per linear meter", cost: 41.00, source: "Rate Book" },
                { item: "H3 treated primed (140x42)", unit: "Per linear meter", cost: 46.00, source: "Rate Book" },
                { item: "H3 treated primed (190x42)", unit: "Per linear meter", cost: 56.00, source: "Rate Book" },
                { item: "H3 treated primed (240x66)", unit: "Per linear meter", cost: 72.00, source: "Rate Book" },
                { item: "H3 truss (Average price)", unit: "Per average truss", cost: 350.00, source: "Rate Book" },
                { item: "Roof sarking", unit: "Per square meter", cost: 10.00, source: "Rate Book" },
                { item: "Wall insulation batts", unit: "Per square meter", cost: 11.00, source: "Rate Book" },
                { item: "R4.0 low insulation batts", unit: "Per square meter", cost: 13.00, source: "Rate Book" },
                { item: "Yellow tongue flooring", unit: "Per square meter", cost: 21.00, source: "Rate Book" },
                { item: "F/C flooring", unit: "Per square meter", cost: 90.00, source: "Rate Book" },
                { item: "H3 treated pine cladding boards", unit: "Per square meter", cost: 80.00, source: "Rate Book" },
                { item: "F/C cladding 6mm recessed edge", unit: "Per square meter", cost: 80.00, source: "Rate Book" },
                { item: "F/C sheets 6mm", unit: "Per square meter", cost: 18.45, source: "Rate Book" },
                { item: "F/C sheets 4.5mm", unit: "Per square meter", cost: 18.00, source: "Rate Book" },
                { item: "Dri Zoro treatment", unit: "Per square meter", cost: 38.00, source: "Rate Book" },
                { item: "Injectable DPC", unit: "Per linear meter", cost: 28.00, source: "Rate Book" },
                { item: "Lifestyle Protection boards", unit: "Per linear meter", cost: 120.00, source: "Rate Book" },
                { item: "Onderbite Protection boards", unit: "Per Board meter", cost: 400.00, source: "Rate Book" },
                { item: "Bondcrete", unit: "Per Board", cost: 14.00, source: "Rate Book" },
                { item: "Davco multi prime", unit: "Per 5 litres", cost: 28.00, source: "Rate Book" },
                { item: "Sealants and termination bars", unit: "Per lot", cost: 420.00, source: "Rate Book" },
                { item: "Rapid set concrete", unit: "Per 20kg bag", cost: 70.00, source: "Rate Book" },
                { item: "Standard concrete", unit: "Per 20kg bag", cost: 20.00, source: "Rate Book" },
                { item: "Flush hollow core door", unit: "Each", cost: 60.00, source: "Rate Book" },
                { item: "Solid core flush door", unit: "Each", cost: 65.00, source: "Rate Book" },
                { item: "Fire rated door", unit: "Each", cost: 200.00, source: "Rate Book" },
                { item: "Entrance lock (External)", unit: "Each", cost: 440.00, source: "Rate Book" },
                { item: "Door lock (Internal)", unit: "Each", cost: 120.00, source: "Rate Book" },
                { item: "RHS steel (25x25)", unit: "Per linear meter", cost: 8.00, source: "Rate Book" },
                { item: "RHS steel (40x40)", unit: "Per linear meter", cost: 15.00, source: "Rate Book" },
                { item: "RHS steel (50x50)", unit: "Per linear meter", cost: 18.00, source: "Rate Book" },
                { item: "RHS steel (100x100)", unit: "Per linear meter", cost: 50.00, source: "Rate Book" },
                { item: "Soil (Bulk)", unit: "Per cubic meter", cost: 100.00, source: "Rate Book" },
                { item: "Custom flashings", unit: "Per linear meter", cost: 90.00, source: "" },
                { item: "Plumbers Dye", unit: "Per test", cost: 66.00, source: "" },
                { item: "LVL 300x45", unit: "Per linear meter", cost: 40.00, source: "" },
                { item: "LVL 250x45", unit: "Per linear meter", cost: 32.00, source: "" },
                { item: "LVL 200x45", unit: "Per linear meter", cost: 21.00, source: "" },
                { item: "LVL 90x45", unit: "Per linear meter", cost: 14.00, source: "" },
                { item: "Glass Replacement (Standard window)", unit: "Per square meter", cost: 320.00, source: "" },
                { item: "Glass Replacement (Standard Door)", unit: "Per square meter", cost: 350.00, source: "" },
                { item: "Glass Replacement (Toughened)", unit: "Per square meter", cost: 450.00, source: "" },
                { item: "Bitumen (Supply and lay)", unit: "Per square meter", cost: 250.00, source: "" },
                { item: "Bitumen (20kg bags)", unit: "Each", cost: 40.00, source: "" },
                { item: "Davco Waterproof Membrane (K10Plus 15kg)", unit: "Each", cost: 250.00, source: "" },
                { item: "Tremco Water Proof Primer (200 EC 20 lt)", unit: "Each", cost: 440.00, source: "" },
                { item: "Tremco Water Proof Membrane (250 GC 20 It Liquid)", unit: "Each", cost: 490.00, source: "" },
                { item: "Sika Ultra Floor Leveller (20kg)", unit: "Each", cost: 77.00, source: "" },
                { item: "Sika MonoTop Range (Spalling Repairs 20kg)", unit: "Each", cost: 95.00, source: "" }
            ],
            // ─── Labour rates ──────────────────────────────────────────────
            // Source of truth: RJBIRD_Pricing_Schedule.xlsx (Peter, 2026-05-20).
            // 29 core trades + 16 specialist services / per-project lines.
            // Per-project + per-inspection + per-quote units are non-hourly
            // but kept in `labour_rates` because they're priced as labour /
            // service work, not equipment hire.
            labour_rates: [
                { trade: "Electrician", unit: "Per hour", rate: 120.00 },
                { trade: "Plumber", unit: "Per hour", rate: 120.00 },
                { trade: "Bricklayer", unit: "Per hour", rate: 88.00 },
                { trade: "Concreter", unit: "Per hour", rate: 88.00 },
                { trade: "Plasterer", unit: "Per hour", rate: 88.00 },
                { trade: "Renderer", unit: "Per hour", rate: 88.00 },
                { trade: "Painter", unit: "Per hour", rate: 88.00 },
                { trade: "Carpenter", unit: "Per hour", rate: 88.00 },
                { trade: "Metal Roofer", unit: "Per hour", rate: 88.00 },
                { trade: "Tile Roofer", unit: "Per hour", rate: 88.00 },
                { trade: "Glazier", unit: "Per hour", rate: 88.00 },
                { trade: "Labourer (General)", unit: "Per hour", rate: 55.00 },
                { trade: "Labourer (Skilled)", unit: "Per hour", rate: 66.00 },
                { trade: "Pebble Creter", unit: "Per hour", rate: 110.00 },
                { trade: "Tiler (Wall and Floor)", unit: "Per hour", rate: 88.00 },
                { trade: "Carpet Layer (Repairs)", unit: "Per hour", rate: 88.00 },
                { trade: "Cabinet Maker", unit: "Per hour", rate: 88.00 },
                { trade: "Handyman", unit: "Per hour", rate: 77.00 },
                { trade: "Fencer", unit: "Per hour", rate: 88.00 },
                { trade: "Locksmith", unit: "Per hour", rate: 110.00 },
                { trade: "Water Proofer", unit: "Per hour", rate: 110.00 },
                { trade: "Landscaper", unit: "Per hour", rate: 88.00 },
                { trade: "Welder (On site)", unit: "Per hour", rate: 120.00 },
                { trade: "Scaffolder", unit: "Per hour", rate: 120.00 },
                { trade: "Excavator / Machine Operator", unit: "Per hour", rate: 99.00 },
                { trade: "Stone Mason", unit: "Per hour", rate: 120.00 },
                { trade: "Flooring Contractor", unit: "Per hour", rate: 99.00 },
                { trade: "Air Conditioning", unit: "Per hour", rate: 160.00 },
                { trade: "Abseiler", unit: "Per hour", rate: 250.00 },
                { trade: "Physical Site Inspection", unit: "Per inspection", rate: 220.00 },
                { trade: "Office Admin", unit: "Per hour", rate: 40.00 },
                { trade: "Re-quoting Fee", unit: "Per quote", rate: 220.00 },
                { trade: "Re-quoting Fee with inspection", unit: "Per quote", rate: 660.00 },
                { trade: "DBPA Registration Fee", unit: "Per project", rate: 2400.00 },
                { trade: "SRD - CDC", unit: "Per project", rate: 7700.00 },
                { trade: "Engineering SRD", unit: "Per project", rate: 11000.00 },
                { trade: "Engineer Report", unit: "Per project", rate: 3300.00 },
                { trade: "RJB Reports", unit: "Per project", rate: 1200.00 },
                { trade: "Pest Control", unit: "Per hour", rate: 220.00 },
                { trade: "TWP - Council Permits", unit: "Each", rate: 220.00 },
                { trade: "Traffic & Pedestrial Mgnmt Plans", unit: "Each", rate: 250.00 },
                { trade: "Trafiic Controller", unit: "Per hour", rate: 55.00 },
                { trade: "Arborist", unit: "Per hour", rate: 250.00 },
                { trade: "Magnesite Removal", unit: "Per square meter", rate: 220.00 },
                { trade: "Asbestos Removal", unit: "Per square meter", rate: 250.00 },
                { trade: "Asbestos Clearance Certificate", unit: "Each", rate: 660.00 }
            ],
            // ─── Equipment & Waste ────────────────────────────────────────
            // Source of truth: RJBIRD_Pricing_Schedule.xlsx (Peter, 2026-05-20).
            // 3 AI-Review project-allowance items + Rate-Book per-cubic-metre
            // + per-project + 8 specific equipment-hire lines.
            equipment: [
                { item: "Waste disposal and site protection", unit: "Each", cost: 150.00, source: "AI Review" },
                { item: "Aluminium tower scaffold hire (5 days)", unit: "Each", cost: 1200.00, source: "AI Review" },
                { item: "Skip bins (4 cubic metres)", unit: "Each", cost: 500.00, source: "AI Review" },
                { item: "Skip bins", unit: "Per cubic meter", cost: 190.00, source: "Rate Book" },
                { item: "Waste and protection allowance", unit: "Per project", cost: 580.00, source: "Rate Book" },
                { item: "Roof Safety Rails", unit: "Per linear meter", cost: 65.00, source: "" },
                { item: "Boom Lift", unit: "Per day", cost: 525.00, source: "" },
                { item: "scissor Lift", unit: "Per day", cost: 680.00, source: "" },
                { item: "Cherry Picker", unit: "Per day", cost: 400.00, source: "" },
                { item: "Standard Scaffolding (14 day hire)", unit: "Per square meter", cost: 330.00, source: "" },
                { item: "Tower Scaffold (8 M platform 14 day hire)", unit: "Per day", cost: 660.00, source: "" },
                { item: "Generator (5.5KVA)", unit: "Per day", cost: 330.00, source: "" },
                { item: "Pressure cleaner (2.600PSI)", unit: "Per day", cost: 295.00, source: "" }
            ],
            subcontractors: [],
            administration: {
                quote_administration_fee: 660.00,
                note: "Allow $660.00 per quote to cover administration fees"
            },
            // ─── Costing configuration ────────────────────────────────────
            // Empty on purpose (2026-11-03): the overhead, contingency and
            // markup-bracket defaults went with the Pricing page's Costing
            // Rules, and the founder-strategy default with its Strategy tab.
            // Nothing seeds them into a business's costing_config; a stored
            // value is read and written back as it is.
            costingConfig: {},
            updated_at: new Date().toISOString()
        };
        return {
            ...legacyDefaults,
            // A default can be returned after a backend outage and is also used
            // for first-run seeding. It must therefore contain no customer's
            // private rate book. RJ Bird's Francis authority is exposed through
            // getRjBirdAuthorityData() and the explicit tenant-checked sync UI.
            material_costs: [],
            labour_rates: [],
            equipment: [],
            subcontractors: [],
            rateCardAuthority: null,
        };
    },

    /**
     * Detached RJ Bird authority candidate. This does not read or write tenant
     * data and must never be used as the global first-run fallback.
     */
    getRjBirdAuthorityData() {
        const authority = cloneReadOnlyPricingValue(rjBirdRateCardAuthority);
        const defaults = this.getDefaultData();
        return {
            ...defaults,
            material_costs: authority.material_costs,
            labour_rates: authority.labour_rates,
            equipment: authority.equipment,
            rateCardAuthority: {
                schema_version: authority.schema_version,
                authority_id: authority.authority_id,
                source_file: authority.source_file,
                source_sha256: authority.source_sha256,
                source_semantics: authority.source_semantics,
                counts: authority.counts,
            },
            administration: {
                ...defaults.administration,
                quote_administration_fee: authority.pricing_methodology
                    .administration_fee_ex_gst_tiers[0].amount_ex_gst,
                tiers: authority.pricing_methodology.administration_fee_ex_gst_tiers,
                note: 'Project administration follows the Francis total-value tiers and is allocated equally across dependent scope items.',
            },
            costingConfig: {
                ...defaults.costingConfig,
                pricingMethodology: authority.pricing_methodology,
                pricingAuthority: {
                    schema_version: authority.schema_version,
                    authority_id: authority.authority_id,
                    source_file: authority.source_file,
                    source_sha256: authority.source_sha256,
                    source_semantics: authority.source_semantics,
                    counts: authority.counts,
                },
            },
        };
    },

    /**
     * Get the costing config (overhead, contingency, margin brackets).
     * Returns the config from saved data, or defaults.
     */
    async getCostingConfig() {
        const data = await this.getData();
        return data?.costingConfig || this.getDefaultData().costingConfig;
    },

    /**
     * Save just the costing config portion.
     * Whole-book read-modify-write → failHard read (never stamp config onto a
     * substituted defaults book) + serialised under the bp write lock so a
     * debounced costing flush can't interleave with another composite writer.
     */
    async saveCostingConfig(config) {
        return withBpWriteLock(async () => {
            const data = await this.getData({ failHard: true });
            data.costingConfig = config;
            if (config?.pricingMethodology?.id === 'francis-sell-band-v1') {
                const tiers = cloneReadOnlyPricingValue(
                    config.pricingMethodology.administration_fee_ex_gst_tiers || [],
                );
                data.administration = {
                    ...(data.administration || {}),
                    quote_administration_fee: Number(tiers[0]?.amount_ex_gst) || 0,
                    tiers,
                    note: 'Project administration follows the Francis total-value tiers and is allocated equally across dependent scope items.',
                };
            }
            await this.saveData(data);
        });
    },

    /**
     * Find the best matching material from business pricing by description.
     * Returns { pricingItemId, matchedName, matchedRate, matchedUnit, confidence } or null.
     * Prefers exact matches, then fuzzy matches sorted by usage_count (most-used first).
     */
    findMaterialMatch(description, pricingData) {
        if (!description || !pricingData?.material_costs?.length) return null;
        const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const desc = norm(description);
        if (!desc || desc.length < 2) return null;
        const trusted = trustedPricingItems(pricingData.material_costs);
        // Pass 1 — exact match (display form).
        for (const m of trusted) {
            const name = norm(m.item);
            if (!name || name.length < 2) continue;
            if (name === desc) return { pricingItemId: m.id || null, matchedName: m.item, matchedRate: m.cost, matchedUnit: m.unit, confidence: 'exact' };
        }
        // Pass 2 — F-04 PATH D: compact-exact (variant-tolerant).
        // Catches "Waterproofer" ↔ "Water Proofer" (whitespace), "Tile" ↔
        // "Tiles" (plural), "Air-Conditioning" ↔ "Air Conditioning" (hyphen),
        // "Cement" ↔ "Cement (20kg)" (parenthetical qualifier). Multi-hit
        // here flows through the same ambiguity resolver as Pass 3.
        const buildMaterialResult = (row, confidence) => ({
            pricingItemId: row.id || null,
            matchedName: row.item,
            matchedRate: row.cost,
            matchedUnit: row.unit,
            confidence,
        });
        const buildMaterialCandidate = (row) => ({
            pricingItemId: row.id || null,
            name: row.item,
            rate: row.cost,
            unit: row.unit,
        });
        const aliasHitMat = _findAliasMatch(description, trusted, (row) => row.item, 'material_costs');
        if (aliasHitMat?.ambiguousAlias) {
            return {
                pricingItemId: null,
                matchedName: null,
                matchedRate: null,
                matchedUnit: null,
                confidence: 'ambiguous',
                candidates: aliasHitMat.candidates,
            };
        }
        if (aliasHitMat?.row) return buildMaterialResult(aliasHitMat.row, 'alias');
        const compactHitsMat = _findCompactMatches(description, trusted, (row) => row.item);
        if (compactHitsMat.length > 0) {
            return _resolveFuzzyCandidates(desc, compactHitsMat, buildMaterialResult, buildMaterialCandidate);
        }
        // Pass 3 — F-04 PATH C: substring fuzzy match. Gather ALL boolean-
        // includes hits, then decide unique-fuzzy vs ambiguous.
        const sorted = [...trusted].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
        const fuzzyHits = [];
        for (const m of sorted) {
            const name = norm(m.item);
            if (!name || name.length < 2) continue;
            if (desc.includes(name) || name.includes(desc)) {
                fuzzyHits.push({ row: m, name });
            }
        }
        return _resolveFuzzyCandidates(desc, fuzzyHits, buildMaterialResult, buildMaterialCandidate);
    },

    /**
     * Find the best matching labour rate from business pricing by trade name.
     * Returns { pricingItemId, matchedName, matchedRate, matchedUnit, confidence } or null.
     * Prefers exact matches, then fuzzy matches sorted by usage_count (most-used first).
     */
    findLabourMatch(tradeName, pricingData, requestedUnit = '', { usePreference = true } = {}) {
        if (!tradeName || !pricingData?.labour_rates?.length) return null;
        const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const trade = norm(tradeName);
        if (!trade || trade.length < 2) return null;
        const trustedRows = trustedPricingItems(pricingData.labour_rates);
        const trusted = usePreference ? preferredLabourCandidates(trustedRows, tradeName,
            requestedUnit, pricingData.costingConfig?.labourRatePreferences || pricingData.costing_config?.labourRatePreferences || {}) : trustedRows;
        const requestedUnitKey = normalisePricingUnitKey(requestedUnit);
        const candidatesForUnit = requestedUnitKey
            ? trusted.filter((row) => normalisePricingUnitKey(row?.unit) === requestedUnitKey)
            : trusted;
        // A day input must never silently inherit the hourly price, or vice
        // versa. No same-unit row is an honest miss that the pricing review can
        // resolve, not permission to apply commercially different arithmetic.
        if (requestedUnitKey && candidatesForUnit.length === 0) return null;
        const buildLabourResult = (row, confidence) => ({
            pricingItemId: row.id || null,
            matchedName: row.trade,
            matchedRate: row.rate,
            matchedUnit: row.unit,
            confidence,
        });
        const buildLabourCandidate = (row) => ({
            pricingItemId: row.id || null,
            name: row.trade,
            rate: row.rate,
            unit: row.unit,
        });
        // Pass 1 — exact match.
        const exactHits = candidatesForUnit
            .filter((row) => norm(row?.trade) === trade)
            .map((row) => ({ row, name: norm(row?.trade) }));
        if (exactHits.length === 1) return buildLabourResult(exactHits[0].row, 'exact');
        if (exactHits.length > 1) {
            return _resolveFuzzyCandidates(trade, exactHits, buildLabourResult, buildLabourCandidate);
        }
        // Pass 2 — F-04 PATH D compact-exact (catches "Waterproofer" ↔ "Water Proofer").
        const aliasHitLab = _findAliasMatch(tradeName, candidatesForUnit, (row) => row.trade, 'labour_rates');
        if (aliasHitLab?.ambiguousAlias) {
            return {
                pricingItemId: null,
                matchedName: null,
                matchedRate: null,
                matchedUnit: null,
                confidence: 'ambiguous',
                candidates: aliasHitLab.candidates,
            };
        }
        if (aliasHitLab?.row) return buildLabourResult(aliasHitLab.row, 'alias');
        const compactHitsLab = _findCompactMatches(tradeName, candidatesForUnit, (row) => row.trade);
        if (compactHitsLab.length > 0) {
            return _resolveFuzzyCandidates(trade, compactHitsLab, buildLabourResult, buildLabourCandidate);
        }
        // Pass 3 — F-04 PATH C substring fuzzy.
        const sorted = [...candidatesForUnit].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
        const fuzzyHits = [];
        for (const l of sorted) {
            const name = norm(l.trade);
            if (!name || name.length < 2) continue;
            if (trade.includes(name) || name.includes(trade)) {
                fuzzyHits.push({ row: l, name });
            }
        }
        return _resolveFuzzyCandidates(trade, fuzzyHits, buildLabourResult, buildLabourCandidate);
    },

    /**
     * Find the best matching equipment item from business pricing.
     * Returns { pricingItemId, matchedName, matchedRate, matchedUnit, confidence } or null.
     */
    findEquipmentMatch(description, pricingData) {
        if (!description || !pricingData?.equipment?.length) return null;
        const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const desc = norm(description);
        if (!desc || desc.length < 2) return null;
        const trusted = trustedPricingItems(pricingData.equipment);
        // Pass 1 — exact.
        for (const e of trusted) {
            const name = norm(e.item || e.name || '');
            if (!name || name.length < 2) continue;
            if (name === desc) return { pricingItemId: e.id || null, matchedName: e.item || e.name, matchedRate: e.cost || e.rate, matchedUnit: e.unit, confidence: 'exact' };
        }
        // Pass 2 — F-04 PATH D compact-exact.
        const buildEquipResult = (row, confidence) => ({
            pricingItemId: row.id || null,
            matchedName: row.item || row.name,
            matchedRate: row.cost || row.rate,
            matchedUnit: row.unit,
            confidence,
        });
        const buildEquipCandidate = (row) => ({
            pricingItemId: row.id || null,
            name: row.item || row.name,
            rate: row.cost || row.rate,
            unit: row.unit,
        });
        const aliasHitEq = _findAliasMatch(description, trusted, (row) => row.item || row.name || '', 'equipment');
        if (aliasHitEq?.ambiguousAlias) {
            return {
                pricingItemId: null,
                matchedName: null,
                matchedRate: null,
                matchedUnit: null,
                confidence: 'ambiguous',
                candidates: aliasHitEq.candidates,
            };
        }
        if (aliasHitEq?.row) return buildEquipResult(aliasHitEq.row, 'alias');
        const compactHitsEq = _findCompactMatches(description, trusted, (row) => row.item || row.name || '');
        if (compactHitsEq.length > 0) {
            return _resolveFuzzyCandidates(desc, compactHitsEq, buildEquipResult, buildEquipCandidate);
        }
        // Pass 3 — F-04 PATH C substring fuzzy.
        const sorted = [...trusted].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
        const fuzzyHits = [];
        for (const e of sorted) {
            const name = norm(e.item || e.name || '');
            if (!name || name.length < 2) continue;
            if (desc.includes(name) || name.includes(desc)) {
                fuzzyHits.push({ row: e, name });
            }
        }
        return _resolveFuzzyCandidates(desc, fuzzyHits, buildEquipResult, buildEquipCandidate);
    },

    /**
     * Find the best matching subcontractor rate from business pricing.
     * Returns { pricingItemId, matchedName, matchedRate, matchedUnit, confidence } or null.
     */
    findSubcontractorMatch(tradeName, pricingData, requestedUnit = '') {
        if (!tradeName || !pricingData?.subcontractors?.length) return null;
        const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const trade = norm(tradeName);
        if (!trade || trade.length < 2) return null;
        const trusted = trustedPricingItems(pricingData.subcontractors);
        const requestedUnitKey = normalisePricingUnitKey(requestedUnit);
        const candidatesForUnit = requestedUnitKey
            ? trusted.filter((row) => normalisePricingUnitKey(row?.unit) === requestedUnitKey)
            : trusted;
        if (requestedUnitKey && candidatesForUnit.length === 0) return null;
        const buildSubResult = (row, confidence) => ({
            pricingItemId: row.id || null,
            matchedName: row.trade || row.name || row.item,
            matchedContractorName: row.contractorName || null,
            matchedSupplierId: row.supplierId || null,
            matchedRate: row.rate ?? row.cost,
            matchedUnit: row.unit,
            confidence,
        });
        const buildSubCandidate = (row) => ({
            pricingItemId: row.id || null,
            name: row.contractorName
                ? `${row.trade || row.name || row.item} · ${row.contractorName}`
                : row.trade || row.name || row.item,
            contractorName: row.contractorName || null,
            supplierId: row.supplierId || null,
            rate: row.rate ?? row.cost,
            unit: row.unit,
        });
        // Pass 1 — exact.
        const exactHits = candidatesForUnit
            .filter((row) => norm(row?.trade || row?.name || row?.item) === trade)
            .map((row) => ({ row, name: trade }));
        if (exactHits.length === 1) return buildSubResult(exactHits[0].row, 'exact');
        if (exactHits.length > 1) {
            return _resolveFuzzyCandidates(trade, exactHits, buildSubResult, buildSubCandidate);
        }
        // Pass 2 — F-04 PATH D compact-exact.
        const aliasHitSub = _findAliasMatch(tradeName, candidatesForUnit, (row) => row.trade || row.name || row.item || '', 'subcontractors');
        if (aliasHitSub?.ambiguousAlias) {
            return {
                pricingItemId: null,
                matchedName: null,
                matchedRate: null,
                matchedUnit: null,
                confidence: 'ambiguous',
                candidates: aliasHitSub.candidates,
            };
        }
        if (aliasHitSub?.row) return buildSubResult(aliasHitSub.row, 'alias');
        const compactHitsSub = _findCompactMatches(tradeName, candidatesForUnit, (row) => row.trade || row.name || row.item || '');
        if (compactHitsSub.length > 0) {
            return _resolveFuzzyCandidates(trade, compactHitsSub, buildSubResult, buildSubCandidate);
        }
        // Pass 3 — F-04 PATH C substring fuzzy.
        const sorted = [...candidatesForUnit].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
        const fuzzyHits = [];
        for (const s of sorted) {
            const name = norm(s.trade || s.name || s.item || '');
            if (!name || name.length < 2) continue;
            if (trade.includes(name) || name.includes(trade)) {
                fuzzyHits.push({ row: s, name });
            }
        }
        return _resolveFuzzyCandidates(trade, fuzzyHits, buildSubResult, buildSubCandidate);
    },

    /**
     * Read-only catalogue match over a single scope item's costBreakdown.
     * Returns the DEDUPED set of `pricingItemId`s whose materials / labour /
     * equipment / subcontractor lines genuinely resolved to a catalogue row
     * (exact / alias / fuzzy). Ambiguous matches return `pricingItemId: null`
     * from the finders and are therefore correctly excluded — an ambiguous
     * line did not confidently "use" a specific rate.
     *
     * QW-USAGE-COUNT-1: this is the HONEST "this rate was used in a quote"
     * signal for the canonical Stage-3 WMS path, which never ran the catalogue
     * matcher (the matcher's only producers — analysis-pipeline Stage 2 and the
     * per-item cost-recompute — are dormant on a normal quote run). It performs
     * NO writes and changes NO rate value: it only reads the finders that
     * already power the most-used-first tie-break. Pure function of
     * (costBreakdown, pricingData).
     *
     * @param {Object|null} costBreakdown - a scope item's costBreakdown (camelCase line arrays)
     * @param {Object} pricingData - BusinessPricingService.getData() snapshot
     * @returns {string[]} deduped matched pricingItemIds (may be empty)
     */
    collectMatchedItemIds(costBreakdown, pricingData) {
        if (!costBreakdown || typeof costBreakdown !== 'object' || !pricingData) return [];
        const ids = new Set();
        const push = (match) => {
            if (match && match.pricingItemId) ids.add(match.pricingItemId);
        };
        const arr = (v) => (Array.isArray(v) ? v : []);
        // Field reads mirror the line builders in pricing-match-applier.js so a
        // line that matches there also matches here (single source of naming).
        for (const m of arr(costBreakdown.materials)) {
            push(this.findMaterialMatch(m?.description || m?.item || '', pricingData));
        }
        for (const l of arr(costBreakdown.labour)) {
            push(this.findLabourMatch(l?.trade || l?.role || '', pricingData, l?.unit || '', { usePreference: false }));
        }
        for (const e of arr(costBreakdown.equipment)) {
            push(this.findEquipmentMatch(e?.description || e?.item || '', pricingData));
        }
        for (const s of arr(costBreakdown.subcontractors)) {
            push(this.findSubcontractorMatch(s?.trade || s?.description || s?.name || '', pricingData, s?.unit || ''));
        }
        return [...ids];
    },

    /**
     * Increment usage count for matched pricing items. Fire-and-forget.
     */
    async batchIncrementUsage(matchedIds) {
        if (!matchedIds?.length) return;
        // DEC-PE-011: when the rate book is read-only during costing, bump the
        // separate per-item counter (targeted UPSERT) instead of re-saving the
        // whole catalogue. Best-effort — usage is telemetry, never blocks costing.
        if (isPricingReadonlyRecomputeEnabled()) {
            try {
                await apiFetch('/business-pricing/usage', {
                    method: 'POST',
                    body: JSON.stringify({ itemIds: matchedIds }),
                });
            } catch (e) {
                console.warn('[Pricing] Usage increment failed (non-fatal):', e?.message);
            }
            return;
        }
        return withBpWriteLock(async () => {
        try {
            const data = await this.getData(); // ensures migration has run
            if (!data) return;
            const idSet = new Set(matchedIds);
            const categories = ['material_costs', 'labour_rates', 'equipment', 'subcontractors'];
            for (const cat of categories) {
                if (!Array.isArray(data[cat])) continue;
                for (const item of data[cat]) {
                    if (item.id && idSet.has(item.id) && isTrustedPricingItem(item)) {
                        item.usage_count = (item.usage_count || 0) + 1;
                        item.last_used_at = new Date().toISOString();
                        item.updated_at = item.last_used_at;
                    }
                }
            }
            await DataService.businessPricing.save({ ...data, id: 'default', updated_at: new Date().toISOString() });
        } catch (e) {
            console.warn('[Pricing] Usage increment failed:', e?.message);
        }
        });
    },

    async addAliasMapping({ category, itemId, alias, reviewedBy = 'user', source = 'rate_trace_panel', reason = '' } = {}) {
        const catKey = normalisePricingCategory(category);
        const cleanAlias = String(alias || '').replace(/\s+/g, ' ').trim();
        if (!catKey || !itemId || cleanAlias.length < 2) {
            return { ok: false, reason: 'category, itemId and alias are required' };
        }

        const data = await this.getData();
        if (!data || !Array.isArray(data[catKey])) {
            return { ok: false, reason: 'pricing category not found' };
        }

        const item = data[catKey].find(row => row?.id === itemId);
        if (!item) return { ok: false, reason: 'pricing item not found' };
        if (!isTrustedPricingItem(item)) return { ok: false, reason: 'only approved pricing rows can receive aliases' };

        const aliasNorm = normaliseAliasValue(cleanAlias);
        const aliasCompact = _compactName(cleanAlias);
        const nameField = pricingNameField(catKey);
        const conflicts = data[catKey].filter(row => {
            if (!row || row.id === itemId || !isTrustedPricingItem(row)) return false;
            const canonical = normaliseAliasValue(row[nameField] || row.name || row.item || row.trade);
            if (canonical === aliasNorm) return true;
            const canonicalCompact = _compactName(row[nameField] || row.name || row.item || row.trade);
            if (aliasCompact && canonicalCompact === aliasCompact) return true;
            return pricingItemAliases(row).some(existingAlias => (
                normaliseAliasValue(existingAlias) === aliasNorm
                || (aliasCompact && _compactName(existingAlias) === aliasCompact)
            ));
        });
        if (conflicts.length > 0) {
            return {
                ok: false,
                reason: `alias already maps to ${pricingItemName(conflicts[0], catKey) || 'another pricing row'}`,
            };
        }

        const aliases = pricingItemAliases(item);
        const alreadyMapped = aliases.some(existingAlias => (
            normaliseAliasValue(existingAlias) === aliasNorm
            || (aliasCompact && _compactName(existingAlias) === aliasCompact)
        ));
        if (!alreadyMapped) {
            item.aliases = [...aliases, cleanAlias];
        } else {
            item.aliases = aliases;
        }
        const now = new Date().toISOString();
        item.updated_at = now;
        item.alias_audit = [
            {
                alias: cleanAlias,
                source,
                reason,
                reviewed_by: reviewedBy,
                reviewed_at: now,
            },
            ...(Array.isArray(item.alias_audit) ? item.alias_audit : []),
        ].slice(0, 12);

        await this.saveData(data);
        return { ok: true, created: !alreadyMapped, item, data };
    },

    async updatePricingItemFromStage5({ category, itemId, rate, unit, reviewedBy = 'user', reason = '' } = {}) {
        const catKey = normalisePricingCategory(category);
        const nextRate = Number(rate);
        const cleanUnit = String(unit || '').replace(/\s+/g, ' ').trim();
        if (!catKey || !itemId) return { ok: false, reason: 'category and itemId are required' };
        if (!Number.isFinite(nextRate) || nextRate <= 0) {
            return { ok: false, reason: 'rate must be a positive number' };
        }

        const data = await this.getData();
        if (!data || !Array.isArray(data[catKey])) {
            return { ok: false, reason: 'pricing category not found' };
        }

        const item = data[catKey].find(row => row?.id === itemId);
        if (!item) return { ok: false, reason: 'pricing item not found' };
        if (!isTrustedPricingItem(item)) {
            return { ok: false, reason: 'only approved pricing rows can be corrected from Stage 5' };
        }

        const previousRate = pricingItemRate(item, catKey);
        const previousUnit = item.unit || '';
        const nextUnit = cleanUnit || previousUnit || (catKey === 'labour_rates' || catKey === 'subcontractors' ? 'Per hour' : 'Each');
        const now = new Date().toISOString();
        const auditReason = reason || 'Corrected from Stage 5 pricing trace';

        recordRateRevision(item, catKey, nextRate, {
            source: 'stage5_rate_trace',
            changedBy: reviewedBy,
            reason: auditReason,
        });
        item.unit = nextUnit;
        item.updated_at = now;
        item.review_status = item.review_status || 'approved';
        item.review_source = 'stage5_rate_trace';
        item.reviewed_at = now;
        item.reviewed_by = reviewedBy;
        item.rate_audit = [
            {
                previous_rate: previousRate,
                next_rate: nextRate,
                previous_unit: previousUnit,
                next_unit: nextUnit,
                source: 'stage5_rate_trace',
                reason: auditReason,
                reviewed_by: reviewedBy,
                reviewed_at: now,
            },
            ...(Array.isArray(item.rate_audit) ? item.rate_audit : []),
        ].slice(0, 12);

        await this.saveData(data);
        return { ok: true, item, data, previousRate, nextRate, previousUnit, nextUnit };
    },

    /**
     * Add a new pricing item discovered from AI output.
     * Deduplicates: if a matching item already exists (by normalised name),
     * increments its usage_count instead of creating a duplicate.
     */
    // DEC-PE-011: post an AI-discovered rate to the review queue instead of
    // writing it into the rate book. The rate enters business_pricing only when
    // the operator accepts the proposal (the backend /accept route). Best-effort.
    async _proposeRate(category, itemData, options = {}) {
        try {
            const candidate = canonicalPricingCandidate(itemData);
            const catKey = normalisePricingCategory(category);
            const nameField = pricingNameField(catKey);
            const costField = pricingRateField(catKey);
            const itemName = candidate?.[nameField] || candidate?.name || candidate?.item || '';
            if (!itemName) return null;
            const aiRate = candidate?.[costField] ?? candidate?.rate ?? candidate?.cost ?? null;
            await apiFetch('/pricing-proposals', {
                method: 'POST',
                body: JSON.stringify({
                    category: catKey,
                    itemName,
                    unit: candidate?.unit || null,
                    aiRate,
                    confidence: options.confidence ?? candidate?.confidence ?? 0,
                    sourceProjectId: options.sourceProjectId || null,
                    discoveredFrom: options.discoveredFrom || 'quote_costing',
                    candidate,
                }),
            });
            return { proposed: true };
        } catch (e) {
            console.warn('[Pricing] Proposal create failed (non-fatal):', e?.message);
            return null;
        }
    },

    async addItemFromAI(category, itemData, options = {}) {
        // DEC-PE-011: when the rate book is read-only during costing, an
        // AI-discovered rate goes to the review queue, NOT the book. Operator-
        // sourced adds (source !== 'ai_discovered') still write the book directly.
        if (isPricingReadonlyRecomputeEnabled() && ((options?.source ?? 'ai_discovered') === 'ai_discovered')) {
            return this._proposeRate(category, itemData, options);
        }
        return withBpWriteLock(async () => {
        try {
            const { updateExistingRate = false, source = 'ai_discovered', reviewMeta = null, createdBy = null, discoveredFrom = 'quote_costing' } = options || {};
            const data = await this.getData();
            if (!data) return null;
            const catKey = normalisePricingCategory(category);
            if (!Array.isArray(data[catKey])) return null;
            const costField = pricingRateField(catKey);

            // Dedup check - normalise and compare against existing items
            const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
            const nameField = pricingNameField(catKey);
            const newName = norm(itemData[nameField] || itemData.name || itemData.item);
            if (newName) {
                const existing = data[catKey].find(item => {
                    if (norm(item[nameField]) === newName) return true;
                    if (catKey === 'subcontractors') {
                        return norm(item.name) === newName || norm(item.item) === newName;
                    }
                    return false;
                });
                if (existing) {
                    const previousRate = existing[costField] ?? existing.rate ?? existing.cost ?? null;
                    if (updateExistingRate) {
                        existing[nameField] = existing[nameField] || itemData[nameField] || itemData.name || itemData.item;
                        existing.unit = itemData.unit || existing.unit;
                        recordRateRevision(existing, catKey, itemData[costField] ?? itemData.rate ?? itemData.cost ?? existing[costField], {
                            source,
                            changedBy: createdBy || (source === 'ai_discovered' ? 'ai' : 'user'),
                            reason: reviewMeta?.reason || reviewMeta?.learningId || 'pricing update',
                        });
                        existing.review_source = source;
                        existing.charge_out_tax_basis = canonicalPricingTaxBasis(
                            itemData?.charge_out_tax_basis
                                ?? itemData?.chargeOutTaxBasis
                                ?? itemData?.rateTaxBasis
                                ?? itemData?.rate_tax_basis
                                ?? existing?.charge_out_tax_basis
                                ?? existing?.chargeOutTaxBasis,
                        );
                        existing.cost_rate_tax_basis = canonicalPricingTaxBasis(
                            itemData?.cost_rate_tax_basis
                                ?? itemData?.costRateTaxBasis
                                ?? existing?.cost_rate_tax_basis
                                ?? existing?.costRateTaxBasis,
                        );
                        existing.reviewed_at = new Date().toISOString();
                        existing.reviewed_by = createdBy || existing.reviewed_by || (source === 'ai_discovered' ? null : 'user');
                        if (source !== 'ai_discovered') {
                            existing.review_status = 'approved';
                            existing.source = existing.source === 'ai_discovered' ? source : (existing.source || source);
                        }
                        if (reviewMeta) {
                            existing.review_meta = {
                                ...(existing.review_meta || {}),
                                ...reviewMeta,
                                previousRate,
                                approvedRate: existing[costField],
                            };
                        }
                    }
                    // Already exists - bump usage_count, don't create duplicate
                    existing.usage_count = (existing.usage_count || 0) + 1;
                    existing.last_used_at = new Date().toISOString();
                    existing.updated_at = existing.last_used_at;
                    await this.saveData(data);
                    return existing;
                }
            }

            const now = new Date().toISOString();
            const newItem = {
                ...itemData,
                charge_out_tax_basis: canonicalPricingTaxBasis(
                    itemData?.charge_out_tax_basis
                        ?? itemData?.chargeOutTaxBasis
                        ?? itemData?.rateTaxBasis
                        ?? itemData?.rate_tax_basis,
                ),
                cost_rate_tax_basis: canonicalPricingTaxBasis(
                    itemData?.cost_rate_tax_basis ?? itemData?.costRateTaxBasis,
                ),
                id: genPricingId(),
                created_at: now,
                updated_at: now,
                usage_count: 1,
                last_used_at: now,
                source,
                created_by: createdBy || (source === 'ai_discovered' ? 'ai' : 'user'),
                pricing_category: catKey,
                previous_rate: null,
                previous_rate_2: null,
                price_history: [],
                ...(source === 'ai_discovered'
                    ? { review_status: 'pending_review', discovered_from: discoveredFrom, reviewed_at: null, reviewed_by: null }
                    : { review_status: 'approved', reviewed_at: now, reviewed_by: createdBy || 'user' }),
                ...(reviewMeta ? { review_meta: reviewMeta } : {}),
            };
            data[catKey].push(newItem);
            await this.saveData(data);
            return newItem;
        } catch (e) {
            console.warn('[Pricing] Auto-create failed:', e?.message);
            return null;
        }
        });
    },

    async listReviewItems() {
        const data = await this.getData();
        const categories = [
            { key: 'material_costs', category: 'materials', label: 'Materials' },
            { key: 'labour_rates', category: 'labour', label: 'Labour' },
            { key: 'equipment', category: 'equipment', label: 'Equipment' },
            { key: 'subcontractors', category: 'subcontractors', label: 'Subcontractors' },
        ];
        return categories.flatMap(cat => (Array.isArray(data?.[cat.key]) ? data[cat.key] : [])
            .filter(isPendingReviewPricingItem)
            .map(item => ({
                ...cat,
                id: item.id,
                name: pricingItemName(item, cat.key),
                unit: item.unit || '',
                rate: pricingItemRate(item, cat.key),
                usage_count: item.usage_count || 0,
                last_used_at: item.last_used_at || null,
                created_at: item.created_at || null,
                discovered_from: item.discovered_from || item.review_meta?.source || '',
                review_meta: item.review_meta || null,
                item,
            })));
    },

    // DEC-PE-011: the pricing-proposals queue the Pricing page reviews. Every
    // AI-discovered rate lands here while qm_pricing_readonly_recompute is on.
    // Rows come back in the listReviewItems shape so one list can show both
    // queues. Throws when the queue cannot be read, so it is never reported
    // as empty.
    async listPendingProposals() {
        const rows = await apiFetch('/pricing-proposals?status=pending');
        const categories = {
            material_costs: { category: 'materials', label: 'Materials' },
            labour_rates: { category: 'labour', label: 'Labour' },
            equipment: { category: 'equipment', label: 'Equipment' },
            subcontractors: { category: 'subcontractors', label: 'Subcontractors' },
        };
        return (Array.isArray(rows) ? rows : [])
            .filter(row => row?.id && categories[row.category])
            .map((row) => {
                const candidate = row.candidate && typeof row.candidate === 'object' ? row.candidate : {};
                const rawRate = row.aiRate;
                const rate = rawRate === null || rawRate === undefined || rawRate === '' ? NaN : Number(rawRate);
                return {
                    key: row.category,
                    ...categories[row.category],
                    id: row.id,
                    name: row.itemName || '',
                    unit: row.unit || candidate.unit || '',
                    rate: Number.isFinite(rate) ? rate : undefined,
                    rate_tax_basis: canonicalPricingTaxBasis(candidate.rateTaxBasis ?? candidate.chargeOutTaxBasis, null),
                    usage_count: 0,
                    created_at: row.createdAt || null,
                    discovered_from: row.discoveredFrom || '',
                    queue: 'proposal',
                    proposal: row,
                };
            });
    },

    // Accept or reject one proposal through the routes the Pricing page uses.
    // Accept writes the rate into the book on the server, so it broadcasts
    // qm-pricing-updated as the page does.
    async reviewProposal({ proposalId, decision, note = '' } = {}) {
        if (!proposalId) return { ok: false, reason: 'proposal id is required' };
        const verb = decision === 'approve' ? 'accept'
            : (decision === 'dismiss' || decision === 'remove') ? 'reject' : null;
        if (!verb) return { ok: false, reason: 'decision must be approve or dismiss' };
        const reason = String(note || '').trim();
        let res;
        try {
            res = await apiFetch(`/pricing-proposals/${encodeURIComponent(proposalId)}/${verb}`, {
                method: 'POST',
                ...(verb === 'reject' && reason ? { body: JSON.stringify({ reason }) } : {}),
            });
        } catch (e) {
            return { ok: false, reason: e?.message || `could not ${verb} the proposal` };
        }
        if (!res?.ok) return { ok: false, reason: res?.error || `could not ${verb} the proposal` };
        if (verb === 'accept' && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            try {
                window.dispatchEvent(new CustomEvent('qm-pricing-updated', {
                    detail: { source: 'BusinessPricingService.reviewProposal', at: Date.now() },
                }));
            } catch { /* CustomEvent unsupported in this env */ }
        }
        return {
            ok: true,
            decision: verb === 'accept' ? 'approved' : 'dismissed',
            proposal: res.proposal || null,
            merged: res.merged === true,
        };
    },

    async reviewDiscoveredItem({ category, itemId, decision, reviewedBy = 'user', note = '' } = {}) {
        const catKey = normalisePricingCategory(category);
        if (!catKey || !itemId) return { ok: false, reason: 'category and itemId are required' };
        // Whole-book read-modify-write: failHard (never operate on a
        // silently-substituted defaults copy) + serialised under the bp lock.
        return withBpWriteLock(async () => {
            const data = await this.getData({ failHard: true });
            if (!data || !Array.isArray(data[catKey])) return { ok: false, reason: 'pricing category not found' };
            const idx = data[catKey].findIndex(item => item?.id === itemId);
            if (idx < 0) return { ok: false, reason: 'pricing item not found' };
            const item = data[catKey][idx];
            const now = new Date().toISOString();
            if (decision === 'dismiss' || decision === 'remove') {
                data[catKey].splice(idx, 1);
                await this.saveData(data);
                return { ok: true, decision: 'dismissed', item };
            }
            if (decision !== 'approve') return { ok: false, reason: 'decision must be approve or dismiss' };
            // An estimate row ("AI Review" provenance or an `estimate` rating)
            // fails the trust rule whatever its review_status says, so its
            // approval makes the rate the builder's own (pricing-trust.js).
            const reviewed = hasEstimateProvenance(item)
                ? approveEstimatePricingItem(item, {
                    action: 'approve',
                    approvedBy: reviewedBy,
                    approvedAt: now,
                    rate: pricingItemRate(item, catKey),
                })
                : {
                    ...item,
                    source: item.source === 'ai_discovered' ? 'approved_ai' : (item.source || 'approved_ai'),
                };
            const approved = {
                ...reviewed,
                review_status: 'approved',
                reviewed_at: now,
                reviewed_by: reviewedBy,
                last_review_note: note,
                updated_at: now,
            };
            data[catKey][idx] = approved;
            await this.saveData(data);
            return { ok: true, decision: 'approved', item: approved, trusted: isTrustedPricingItem(approved) };
        });
    },
};
